import { apiEndpoints } from "@config/env";
import { getData, isGetDataCancelled } from "../../../utils/global";
import { debug as logDebug, error as logError } from "../../../utils/console";

/** Sentinel message for a superseded/aborted request. Callers must not surface it as an error. */
export const FLEET_RANKING_CANCELLED_MESSAGE = "Request cancelled";

export type FleetRankingMode = "upwind" | "downwind" | "tack" | "gybe";
export type FleetRankingGroupBy = "event" | "tws_bin" | "config" | "day";
export type FleetRankingDisplayMode = "ranking" | "value";

/** Minimum samples required before a bucket point is shown or ranked. */
export const MIN_FLEET_RANKING_SAMPLE_COUNT = 3;
export const FLEET_RANKING_TWS_BUCKET_VALUES = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55] as const;

export function hasEnoughFleetRankingSamples(sampleCount: number): boolean {
  return sampleCount >= MIN_FLEET_RANKING_SAMPLE_COUNT;
}

export interface FleetRankingTrendRow {
  source_name: string;
  bucket_key: string;
  bucket_label: string;
  bucket_order: string | number | null;
  avg_vmg_percent: number | null;
  sample_count: number;
}

export interface FleetRankingBucket {
  key: string;
  label: string;
  order: number;
}

export interface FleetRankingSeriesPoint {
  bucketKey: string;
  bucketLabel: string;
  bucketOrder: number;
  yValue: number | null;
  avgVmgPercent: number | null;
  sampleCount: number;
  /** Boats ranked in this bucket — the denominator of the rank, so "8th of 8" is auditable. */
  rankPoolSize: number;
}

export interface FleetRankingSeries {
  sourceName: string;
  points: FleetRankingSeriesPoint[];
}

export interface FetchFleetRankingTrendParams {
  className: string;
  projectId: string | number;
  startDate: string;
  endDate: string;
  mode: FleetRankingMode;
  groupBy: FleetRankingGroupBy;
  sourceNames?: string[];
  signal?: AbortSignal;
}

function normalizeDateYmd(value: string): string {
  return String(value ?? "").replace(/[-/]/g, "").trim();
}

export type FleetRankingRowsByMode = Record<FleetRankingMode, FleetRankingTrendRow[]>;

function parseFleetRankingTrendRows(raw: unknown[]): FleetRankingTrendRow[] {
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    const avgRaw = r.avg_vmg_percent;
    const avg =
      avgRaw == null || avgRaw === ""
        ? null
        : Number(avgRaw);
    return {
      source_name: String(r.source_name ?? ""),
      bucket_key: String(r.bucket_key ?? ""),
      bucket_label: String(r.bucket_label ?? r.bucket_key ?? ""),
      bucket_order: (r.bucket_order as string | number | null) ?? null,
      avg_vmg_percent: Number.isFinite(avg) ? avg : null,
      sample_count: Number(r.sample_count ?? 0),
    };
  });
}

export interface FetchFleetRankingTrendAllParams {
  className: string;
  projectId: string | number;
  startDate: string;
  endDate: string;
  groupBy: FleetRankingGroupBy;
  sourceNames?: string[];
  signal?: AbortSignal;
  /** Required when group_by is day: scopes the drill-down to this event name. */
  event?: string;
}

export async function fetchFleetRankingTrendAll(
  params: FetchFleetRankingTrendAllParams
): Promise<{ ok: true; rowsByMode: FleetRankingRowsByMode } | { ok: false; message: string }> {
  const startYmd = normalizeDateYmd(params.startDate);
  const endYmd = normalizeDateYmd(params.endDate);
  if (startYmd.length !== 8 || endYmd.length !== 8) {
    return { ok: false, message: "Start and end dates are required" };
  }

  let url =
    `${apiEndpoints.app.data}/fleet-ranking-trend?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(String(params.projectId))}` +
    `&start_date=${encodeURIComponent(startYmd)}` +
    `&end_date=${encodeURIComponent(endYmd)}` +
    `&mode=all` +
    `&group_by=${encodeURIComponent(params.groupBy)}`;

  if (params.sourceNames != null && params.sourceNames.length > 0) {
    url += `&source_names=${encodeURIComponent(JSON.stringify(params.sourceNames))}`;
  }

  if (params.event != null && params.event.trim() !== "") {
    url += `&event=${encodeURIComponent(params.event.trim())}`;
  }

  try {
    const result = await getData(url, params.signal);
    if (params.signal?.aborted || isGetDataCancelled(result)) {
      return { ok: false, message: FLEET_RANKING_CANCELLED_MESSAGE };
    }
    if (!result?.success) {
      return { ok: false, message: result?.message ?? "Failed to load fleet ranking trend" };
    }
    const payload = (result.data ?? {}) as { rows_by_mode?: Partial<Record<FleetRankingMode, unknown[]>> };
    const rawByMode = payload.rows_by_mode ?? {};
    const rowsByMode: FleetRankingRowsByMode = {
      upwind: parseFleetRankingTrendRows(Array.isArray(rawByMode.upwind) ? rawByMode.upwind : []),
      downwind: parseFleetRankingTrendRows(Array.isArray(rawByMode.downwind) ? rawByMode.downwind : []),
      tack: parseFleetRankingTrendRows(Array.isArray(rawByMode.tack) ? rawByMode.tack : []),
      gybe: parseFleetRankingTrendRows(Array.isArray(rawByMode.gybe) ? rawByMode.gybe : []),
    };
    const totalRows = Object.values(rowsByMode).reduce((sum, rows) => sum + rows.length, 0);
    logDebug("fleetRankingTrend: fetched batch", {
      groupBy: params.groupBy,
      totalRows,
    });
    return { ok: true, rowsByMode };
  } catch (err) {
    if (params.signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
      return { ok: false, message: FLEET_RANKING_CANCELLED_MESSAGE };
    }
    logError("fleetRankingTrend: batch fetch failed", err);
    return {
      ok: false,
      message: err instanceof Error ? err.message : "Failed to load fleet ranking trend",
    };
  }
}

export async function fetchFleetRankingTrend(
  params: FetchFleetRankingTrendParams
): Promise<{ ok: true; rows: FleetRankingTrendRow[] } | { ok: false; message: string }> {
  const startYmd = normalizeDateYmd(params.startDate);
  const endYmd = normalizeDateYmd(params.endDate);
  if (startYmd.length !== 8 || endYmd.length !== 8) {
    return { ok: false, message: "Start and end dates are required" };
  }

  let url =
    `${apiEndpoints.app.data}/fleet-ranking-trend?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(String(params.projectId))}` +
    `&start_date=${encodeURIComponent(startYmd)}` +
    `&end_date=${encodeURIComponent(endYmd)}` +
    `&mode=${encodeURIComponent(params.mode)}` +
    `&group_by=${encodeURIComponent(params.groupBy)}`;

  if (params.sourceNames != null && params.sourceNames.length > 0) {
    url += `&source_names=${encodeURIComponent(JSON.stringify(params.sourceNames))}`;
  }

  try {
    const result = await getData(url, params.signal);
    if (params.signal?.aborted || isGetDataCancelled(result)) {
      return { ok: false, message: FLEET_RANKING_CANCELLED_MESSAGE };
    }
    if (!result?.success) {
      return { ok: false, message: result?.message ?? "Failed to load fleet ranking trend" };
    }
    const payload = (result.data ?? {}) as { rows?: unknown[] };
    const raw = Array.isArray(payload.rows) ? payload.rows : [];
    const rows = parseFleetRankingTrendRows(raw);
    logDebug("fleetRankingTrend: fetched", {
      mode: params.mode,
      groupBy: params.groupBy,
      rows: rows.length,
    });
    return { ok: true, rows };
  } catch (err) {
    if (params.signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
      return { ok: false, message: FLEET_RANKING_CANCELLED_MESSAGE };
    }
    logError("fleetRankingTrend: fetch failed", err);
    return {
      ok: false,
      message: err instanceof Error ? err.message : "Failed to load fleet ranking trend",
    };
  }
}

function bucketOrderValue(order: string | number | null | undefined): number {
  if (order == null) return Number.MAX_SAFE_INTEGER;
  if (typeof order === "number" && Number.isFinite(order)) return order;

  const str = String(order).trim();
  const isoDate = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return Number(`${isoDate[1]}${isoDate[2]}${isoDate[3]}`);
  }

  // Whole-number orders (e.g. the day bucket's YYMMDD) must be taken in full — the
  // 8-digit slice below is for date-prefixed strings and would drop the hour.
  if (/^\d+$/.test(str)) {
    const asInt = Number(str);
    if (Number.isSafeInteger(asInt)) return asInt;
  }

  const digitsOnly = str.replace(/\D/g, "");
  if (digitsOnly.length >= 8) {
    const ymd = Number(digitsOnly.slice(0, 8));
    if (Number.isFinite(ymd)) return ymd;
  }

  const asNum = Number(str);
  if (Number.isFinite(asNum)) return asNum;

  return Number.MAX_SAFE_INTEGER;
}

export function buildFleetRankingBuckets(rows: FleetRankingTrendRow[]): FleetRankingBucket[] {
  const map = new Map<string, FleetRankingBucket>();
  for (const row of rows) {
    const key = row.bucket_key;
    if (!key) continue;
    const existing = map.get(key);
    const order = bucketOrderValue(row.bucket_order);
    if (!existing) {
      map.set(key, { key, label: row.bucket_label || key, order });
    } else if (order < existing.order) {
      map.set(key, { ...existing, order, label: row.bucket_label || existing.label });
    }
  }
  return [...map.values()].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export function buildFleetRankingTwsBuckets(maxBucket?: number): FleetRankingBucket[] {
  const max =
    maxBucket != null && Number.isFinite(maxBucket)
      ? Math.max(Math.min(maxBucket, FLEET_RANKING_TWS_BUCKET_VALUES.at(-1) ?? maxBucket), FLEET_RANKING_TWS_BUCKET_VALUES[0])
      : FLEET_RANKING_TWS_BUCKET_VALUES.at(-1);

  return FLEET_RANKING_TWS_BUCKET_VALUES.filter((bucket) => max == null || bucket <= max).map((bucket) => ({
    key: String(bucket),
    label: `${bucket} kph`,
    order: bucket,
  }));
}

/**
 * Rank key for a boat. Source names arrive straight from the database, so a stray space or a
 * case variant would otherwise occupy a second rank slot and inflate the fleet size.
 */
export function fleetRankingSourceKey(sourceName: string): string {
  return sourceName.trim().toLowerCase();
}

/**
 * One row per boat per bucket.
 *
 * The server already groups by source_name, but two spellings of the same boat ("NZL" / "nzl ")
 * arrive as two rows and would otherwise take two rank slots — making the fleet look bigger than
 * it is. Collapsed on the normalized name, keeping the row with the most samples (first seen wins
 * a tie) so the ranked value and the tooltip's value are always the same row, and keeping the
 * first-seen spelling as the label so the series name does not depend on which row won.
 */
export function dedupeFleetRankingRows<
  T extends { source_name: string; bucket_key: string; sample_count: number },
>(rows: T[]): T[] {
  const byKey = new Map<string, T>();
  for (const row of rows) {
    const source = row.source_name.trim();
    if (!source || !row.bucket_key) continue;
    const key = `${fleetRankingSourceKey(source)}\u001f${row.bucket_key}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...row, source_name: source });
    } else if (row.sample_count > existing.sample_count) {
      byKey.set(key, { ...row, source_name: existing.source_name });
    }
  }
  return [...byKey.values()];
}

export type FleetRankingRankInput = {
  bucketKey: string;
  sourceKey: string;
  value: number;
};

export interface FleetRankingBucketRanks {
  /** sourceKey -> rank (1 is best). */
  rankBySource: Map<string, number>;
  /** Distinct boats ranked in this bucket. */
  poolSize: number;
}

/**
 * Ranks per bucket, shared by the VMG and prestart charts.
 *
 * Competition ranking: equal values share the better rank and the next rank skips, so two boats
 * on the same average are both 2nd and the next is 4th rather than being separated by sort order.
 * `direction` is "high" when a larger value is better.
 */
export function buildRankByBucket(
  inputs: FleetRankingRankInput[],
  direction: "high" | "low"
): Map<string, FleetRankingBucketRanks> {
  const byBucket = new Map<string, FleetRankingRankInput[]>();
  for (const input of inputs) {
    if (!input.bucketKey || !input.sourceKey) continue;
    if (!Number.isFinite(input.value)) continue;
    if (!byBucket.has(input.bucketKey)) byBucket.set(input.bucketKey, []);
    byBucket.get(input.bucketKey)!.push(input);
  }

  const out = new Map<string, FleetRankingBucketRanks>();
  for (const [bucketKey, bucketInputs] of byBucket) {
    const sorted = [...bucketInputs].sort((a, b) =>
      direction === "high" ? b.value - a.value : a.value - b.value
    );
    const rankBySource = new Map<string, number>();
    let rank = 0;
    let previousValue: number | null = null;
    sorted.forEach((input, index) => {
      if (previousValue == null || input.value !== previousValue) {
        rank = index + 1;
        previousValue = input.value;
      }
      rankBySource.set(input.sourceKey, rank);
    });
    // Distinct boats, not rows — the denominator must never overstate the fleet.
    out.set(bucketKey, { rankBySource, poolSize: rankBySource.size });
  }
  return out;
}

export function buildFleetRankingSeries(
  rawRows: FleetRankingTrendRow[],
  displayMode: FleetRankingDisplayMode,
  bucketOverride?: FleetRankingBucket[],
  /** Override the default ≥3 sample gate. */
  minSampleCount?: number
): { buckets: FleetRankingBucket[]; series: FleetRankingSeries[] } {
  const rows = dedupeFleetRankingRows(rawRows);
  const buckets = bucketOverride != null && rows.length > 0 ? bucketOverride : buildFleetRankingBuckets(rows);
  const sampleThreshold =
    minSampleCount != null && Number.isFinite(minSampleCount)
      ? Math.max(1, Math.floor(minSampleCount))
      : MIN_FLEET_RANKING_SAMPLE_COUNT;
  const hasEnoughSamples = (sampleCount: number): boolean => sampleCount >= sampleThreshold;
  // Keyed on the normalized name; `displayName` keeps the original for labels and source colors.
  const bySource = new Map<
    string,
    { displayName: string; rowsByBucket: Map<string, FleetRankingTrendRow> }
  >();

  for (const row of rows) {
    const source = row.source_name.trim();
    if (!source || !row.bucket_key) continue;
    const key = fleetRankingSourceKey(source);
    if (!bySource.has(key)) {
      bySource.set(key, { displayName: source, rowsByBucket: new Map() });
    }
    bySource.get(key)!.rowsByBucket.set(row.bucket_key, row);
  }

  const rankByBucket =
    displayMode === "ranking"
      ? buildRankByBucket(
          rows
            .filter(
              (r) =>
                r.avg_vmg_percent != null &&
                Number.isFinite(r.avg_vmg_percent) &&
                hasEnoughSamples(r.sample_count)
            )
            .map((r) => ({
              bucketKey: r.bucket_key,
              sourceKey: fleetRankingSourceKey(r.source_name),
              value: r.avg_vmg_percent as number,
            })),
          "high"
        )
      : new Map<string, FleetRankingBucketRanks>();

  const series: FleetRankingSeries[] = [...bySource.entries()]
    .sort(([, a], [, b]) => a.displayName.localeCompare(b.displayName))
    .map(([sourceKey, { displayName, rowsByBucket }]) => {
      const points: FleetRankingSeriesPoint[] = buckets.map((bucket) => {
        const row = rowsByBucket.get(bucket.key);
        const avg = row?.avg_vmg_percent ?? null;
        const sampleCount = row?.sample_count ?? 0;
        const bucketRanks = rankByBucket.get(bucket.key);
        const rank = bucketRanks?.rankBySource.get(sourceKey) ?? null;
        const enoughSamples = hasEnoughSamples(sampleCount);
        const yValue =
          !enoughSamples
            ? null
            : displayMode === "ranking"
              ? rank
              : avg != null && Number.isFinite(avg)
                ? avg
                : null;
        return {
          bucketKey: bucket.key,
          bucketLabel: bucket.label,
          bucketOrder: bucket.order,
          yValue,
          avgVmgPercent: avg,
          sampleCount,
          rankPoolSize: bucketRanks?.poolSize ?? 0,
        };
      });
      return { sourceName: displayName, points };
    });

  return { buckets, series };
}

export function fleetRankingModeLabel(mode: FleetRankingMode): string {
  switch (mode) {
    case "upwind":
      return "Phase Upwind";
    case "downwind":
      return "Phase Downwind";
    case "tack":
      return "Phase Tacks";
    case "gybe":
      return "Phase Gybes";
    default:
      return mode;
  }
}

export function fleetRankingModeFromDropdown(item: string): FleetRankingMode {
  const normalized = item.trim().toUpperCase();
  if (normalized === "DOWNWIND") return "downwind";
  if (normalized === "TACKS") return "tack";
  if (normalized === "GYBES") return "gybe";
  return "upwind";
}

export function fleetRankingGroupByFromDropdown(item: string): FleetRankingGroupBy {
  const normalized = item.trim().toUpperCase();
  if (normalized === "TWS BIN") return "tws_bin";
  if (normalized === "CONFIG") return "config";
  return "event";
}

export function fleetRankingDisplayFromDropdown(item: string): FleetRankingDisplayMode {
  return item.trim().toUpperCase() === "VALUE" ? "value" : "ranking";
}

export function fleetRankingGroupByLabel(groupBy: FleetRankingGroupBy): string {
  if (groupBy === "event") return "Event";
  if (groupBy === "tws_bin") return "TWS Bin";
  if (groupBy === "day") return "Day";
  return "Config";
}

/**
 * X-axis title. Day buckets are labelled with the YYMMDD integer, so the axis says what the digits
 * mean. The date is the venue-local sailing date (see server_app/utils/fleetDayBucketSql.js).
 */
export function fleetRankingGroupByAxisLabel(groupBy: FleetRankingGroupBy): string {
  if (groupBy === "day") return "Day (YYMMDD)";
  return fleetRankingGroupByLabel(groupBy);
}

export function fleetRankingGroupByHeader(groupBy: FleetRankingGroupBy): string {
  if (groupBy === "event") return "EVENT";
  if (groupBy === "tws_bin") return "TWS BIN";
  if (groupBy === "day") return "DAY";
  return "CONFIG";
}

export function fleetRankingChartTitle(
  mode: FleetRankingMode,
  groupBy: FleetRankingGroupBy,
  displayMode: FleetRankingDisplayMode
): string {
  const modeLabel = fleetRankingModeLabel(mode);
  const groupLabel = fleetRankingGroupByLabel(groupBy);
  const metricLabel = displayMode === "ranking" ? "Ranking Trend" : "VMG % Trend";
  return `Fleet ${modeLabel} ${groupLabel} ${metricLabel}`;
}
